import { useMemo, useRef } from 'react'
import { useHexStreams, useStore } from '@/store'
import { getRegionLabel } from '@/constants'
import { useCatalogStore } from '@/store/catalogStore'
import { orderHexItems } from '@/views/hexView/layers'
import type { SearchSliceNode, StreamSliceNode } from '@/analysis/searchContract'
import {
  createStreamRetainState,
  retainRecentStreamNodes,
  toNoDataStreamShell,
} from '@/hooks/retainRecentStreamNodes'
import { mergeCatalogStreamMembership } from '@/hooks/catalogStreamMembership'

export interface HexItem {
  id: string
  label: string
  tick: unknown
}

function labelFor(
  dimension: string,
  dimensionValue: string,
  names: { games: Map<string, string>; studios: Map<string, string>; brands: Map<string, string> },
): string {
  if (dimension === 'continent') return getRegionLabel(dimensionValue)
  if (dimension === 'country') return getRegionLabel(dimensionValue)
  if (dimension === 'game') return names.games.get(dimensionValue) ?? dimensionValue
  if (dimension === 'studio') return names.studios.get(dimensionValue) ?? dimensionValue
  if (dimension === 'brand') return names.brands.get(dimensionValue) ?? dimensionValue
  return dimensionValue
}

/** Belt-and-suspenders: null metrics when snapshot end is outside envelope display bounds. */
function alignToDisplayWindow(
  nodes: SearchSliceNode[],
  fromMs: number | null | undefined,
  toMs: number | null | undefined,
): SearchSliceNode[] {
  if (fromMs == null || toMs == null || !Number.isFinite(fromMs) || !Number.isFinite(toMs)) {
    return nodes
  }
  return nodes.map((n) => {
    if (n.kind !== 'stream') return n
    const end = Number(n.windowEndMs)
    if (Number.isFinite(end) && end >= fromMs && end <= toMs) return n
    return toNoDataStreamShell(n as StreamSliceNode)
  })
}

export function useHexItems(): HexItem[] {
  const dimension = useStore((s) => s.dimension)
  const includeInactive = useStore((s) => s.includeInactive)
  const activeDataKey = useStore((s) => s.activeDataKey)
  const drillNode = useStore((s) => s.drillNode)
  const filters = useStore((s) => s.filters)
  const nowTs = useStore((s) => s.nowTs)
  const searchResult = useStore((s) => s.searchResult)
  const searchResultDrill = useStore((s) => s.searchResultDrill)
  const hexStreams = useHexStreams()
  const searchNodes = hexStreams
  const games = useCatalogStore((s) => s.games)
  const studios = useCatalogStore((s) => s.studios)
  const brands = useCatalogStore((s) => s.brands)
  const catalogStreams = useCatalogStore((s) => s.streams)
  const catalogLoaded = useCatalogStore((s) => s.loaded)
  const names = useMemo(() => ({
    games: new Map(games.map((g) => [g.code, g.name])),
    studios: new Map(studios.map((s) => [s.code, s.name])),
    brands: new Map(brands.map((b) => [b.code, b.name])),
  }), [games, studios, brands])

  const retainState = useRef(createStreamRetainState())

  const scopeKey = useMemo(() => {
    return JSON.stringify({
      activeDataKey,
      dimension,
      drillNode,
      studio: filters.studio,
      game: filters.game,
      brand: filters.brand,
      continent: filters.continent,
      country: filters.country,
      city: filters.city,
    })
  }, [activeDataKey, dimension, drillNode, filters])

  const displayBounds = useMemo(() => {
    const env = drillNode && searchResultDrill ? searchResultDrill : searchResult
    return { fromMs: env?.fromMs ?? null, toMs: env?.toMs ?? null }
  }, [drillNode, searchResult, searchResultDrill])

  const retainedStreams = useMemo(() => {
    const incoming = (Array.isArray(hexStreams) ? hexStreams : []) as SearchSliceNode[]
    const aligned = alignToDisplayWindow(incoming, displayBounds.fromMs, displayBounds.toMs)
    const nowMs = nowTs || Date.now()

    if (dimension === 'stream' && includeInactive) {
      // Do not fall back to live-only while catalog is still loading.
      if (!catalogLoaded) return []
      const live = aligned.filter((n): n is StreamSliceNode => n.kind === 'stream')
      const nonStreams = aligned.filter((n) => n.kind !== 'stream')
      const merged = mergeCatalogStreamMembership(live, catalogStreams, filters, nowMs)
      return [...nonStreams, ...merged]
    }

    // Soft-retain only under Active only — Include inactive uses catalog membership.
    return retainRecentStreamNodes(aligned, {
      enabled: dimension === 'stream' && !includeInactive,
      nowMs,
      scopeKey,
      state: retainState.current,
    })
  }, [
    hexStreams,
    dimension,
    includeInactive,
    nowTs,
    scopeKey,
    displayBounds,
    catalogStreams,
    catalogLoaded,
    filters,
  ])

  const liveItems = useMemo(() => {
    if (dimension === 'stream') {
      return retainedStreams.map((row: Record<string, unknown>) => {
        const id = String(row.streamId ?? row.value ?? '').trim()
        const label = String(row.label ?? row.streamId ?? row.value ?? id)
        return { id, label, tick: row }
      }).filter((h) => h.id !== '')
    }

    const rows = Array.isArray(searchNodes) ? searchNodes : []
    return rows
      .map((row: Record<string, unknown>) => {
        const id = String(row.value ?? row.id ?? '').trim()
        return {
          id,
          label: labelFor(dimension, id, names),
          tick: row,
        }
      })
      .filter((item) => item.id !== '')
  }, [dimension, retainedStreams, searchNodes, names])

  return useMemo(() => orderHexItems(liveItems), [liveItems])
}

/** True while Include inactive needs catalog inventory before painting stream tiles. */
export function useHexInactiveCatalogPending(): boolean {
  const dimension = useStore((s) => s.dimension)
  const includeInactive = useStore((s) => s.includeInactive)
  const catalogLoaded = useCatalogStore((s) => s.loaded)
  return dimension === 'stream' && includeInactive && !catalogLoaded
}
